import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="surface p-8 max-w-md text-center">
          <h2 className="text-xl font-semibold">Invalid setup link</h2>
          <p className="text-sm text-muted-foreground mt-2">
            This link is missing the required token. Please use the link from your email or request a new one.
          </p>
          <Link to="/forgot-password" className="btn-primary mt-6 inline-flex">Request new link</Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (pw1.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (pw1 !== pw2) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/set-password", { token, new_password: pw1 });
      setUser(data);
      setDone(true);
      setTimeout(() => navigate("/dashboard", { replace: true }), 800);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-sm">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Leynix</div>
          <h2 className="text-2xl font-semibold tracking-tight">Set your password</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Choose a strong password to secure your account.
          </p>
        </div>

        {done ? (
          <div className="surface p-6 text-center" data-testid="set-password-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
            <div className="font-semibold">Password set</div>
            <div className="text-sm text-muted-foreground mt-1">Redirecting to your dashboard…</div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="set-password-form">
            <div>
              <label className="text-sm font-medium block mb-1.5">New password</label>
              <input
                type="password"
                value={pw1}
                onChange={(e) => setPw1(e.target.value)}
                className="input-base"
                autoComplete="new-password"
                placeholder="At least 8 characters"
                required
                data-testid="set-password-pw1"
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1.5">Confirm password</label>
              <input
                type="password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                className="input-base"
                autoComplete="new-password"
                placeholder="Type it again"
                required
                data-testid="set-password-pw2"
              />
            </div>
            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="set-password-error">
                {error}
              </div>
            )}
            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="set-password-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
